import { Component, input, output, ChangeDetectionStrategy, effect } from '@angular/core';
import { AbstractDynamicReportViewComponent } from '../../abstract/abstract-dynamic-report-view.component';
import { GroupedDataRecordModel, ReportRecord } from '../../models/report.model';
import { groupByProperty, mapImageColorToRecordItems, sortByParentPosition } from '../../shared';
import { EditableFieldsService } from '../editable-fields.service';
import { NgClass } from '@angular/common';
import { CompareActionsToolbarComponent } from '../comparison-view/compare-actions-toolbar/compare-actions-toolbar.component';
import { CondensedHeaderComponent } from './condensed-header/condensed-header.component';
import { CondensedBodyComponent } from './condensed-body/condensed-body.component';

import { CondensedRecordDataPipe } from './condensed-view-record.pipe';
import { L10nTranslatePipe } from 'angular-l10n';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-condensed-view',
  templateUrl: './condensed-view.component.html',
  styleUrl: './condensed-view.component.scss',
  providers: [EditableFieldsService],
  imports: [
    CompareActionsToolbarComponent,
    NgClass,
    CondensedHeaderComponent,
    CondensedBodyComponent,
    CondensedRecordDataPipe,
    L10nTranslatePipe
],
})
export class CondensedViewComponent extends AbstractDynamicReportViewComponent {
  readonly maxRecordsPerPage = input<number>(6);
  readonly removeRecord = output<ReportRecord>();

  reportItems: GroupedDataRecordModel[];

  constructor() {
    super();
    effect(() => {
      const reportData = this.reportData();
      const reportOptions = this.reportOptions();
      if (reportData && reportOptions) {
        this.makeReportList();
      }
    });
  }

  private makeReportList() {
    const reportOptions = this.reportOptions();
    const reportData = this.reportData();
    const { groupByBuilding } = reportOptions;
    const { records } = reportData;
    const mappedRecords = groupByBuilding ? groupByProperty(records) : sortByParentPosition(records);
    this.reportItems = mapImageColorToRecordItems(mappedRecords);
    // TODO(ppt): add configurations for highlights and reps (contacts) hiding
    // Validation added because sometimes we are getting report views which are not configured.
    // Apply PDF logic
    if (this.isPDF()) {
      this.reportItems = this.splitPdfRecords(this.reportItems);
    }
  }

  private splitPdfRecords(reportItems: GroupedDataRecordModel[]): GroupedDataRecordModel[] {
    return reportItems.reduce((acc, reportItem) => {
      return [...acc, ...this.multiplyReportRecords(reportItem)];
    }, []);
  }

  /**
   * Receives a report item and recursively clones and multiply the report item
   * depending on the max number of records inside it, returning an array of report
   * items.
   */
  private multiplyReportRecords(reportItem: GroupedDataRecordModel): GroupedDataRecordModel[] {
    let resultData = [];
    if (reportItem.data.length > this.maxRecordsPerPage()) {
      const reportItemWLeftovers = Object.assign({}, reportItem);
      const tmpReportItem = Object.assign({}, reportItem);
      tmpReportItem.data = [...tmpReportItem.data];
      reportItemWLeftovers.data = tmpReportItem.data.splice(6);
      resultData.push(tmpReportItem, ...this.multiplyReportRecords(reportItemWLeftovers));
    } else {
      resultData = [reportItem];
    }
    return resultData;
  }

  removeRecordFromReport(reportItem: ReportRecord, recordIndex: number) {
    const recordId = reportItem.recordId ? reportItem.recordId : reportItem.data[recordIndex].recordId;
    this.removeRecord.emit({ ...reportItem, recordId });
  }
}
